import { MicroSessionTileFromHash } from "@components/film/MicroSessionTile"
import { useFerve } from "@features/ferve/FerveProvider"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import * as T from "@radix-ui/react-tooltip"
import { tv } from "@utils/tw"
import { AnimatePresence, motion } from "motion/react"
import { ReactNode, useEffect } from "react"
import { useSnapshot } from "valtio"

type Props = {
  children: ReactNode
}

export function WishlistNotification(props: Props) {
  if (env.admin) return props.children
  const styles = tooltipStyles()
  const carpet = useFlyingCarpet()

  const state = useFerve().wishlist.notifications
  const notifications = useSnapshot(state).items

  const addedItems = Object.entries(notifications).filter(([key, value]) => value.type === "added")
  const removedItems = Object.entries(notifications).filter(([key, value]) => value.type === "removed")

  const hasItems = addedItems.length > 0 || removedItems.length > 0

  const dismiss = () => {
    state.items = {}
  }

  useEffect(() => {
    if (hasItems && carpet.isOpen) {
      dismiss()
    }
  }, [carpet.isOpen, hasItems])

  useEffect(() => {
    if (!hasItems) return
    const timeout = setTimeout(() => {
      dismiss()
    }, 3000)
    return () => {
      clearTimeout(timeout)
    }
  }, [notifications])

  return (
    <T.Root open={hasItems && !carpet.isOpen} delayDuration={0}>
      <T.Trigger asChild {...props}>
        {props.children}
      </T.Trigger>
      <T.Portal forceMount>
        <T.Content
          className={styles.content()}
          side="bottom"
          align="center"
          avoidCollisions
          sideOffset={0}
          collisionPadding={16}
        >
          <AnimatePresence mode="wait">
            {hasItems && (
              <motion.div
                className={styles.inner()}
                initial="hide"
                animate="show"
                exit="hide"
                variants={{ hide: { opacity: 0, scale: 0.8, y: -10 }, show: { opacity: 1, scale: 1, y: 0 } }}
                onClick={() => {
                  dismiss()
                  carpet.launch("planner")
                }}
              >
                {addedItems.length > 0 && <div className={styles.heading()}>Added to your watchlist</div>}
                {addedItems.map(([key]) => (
                  <MicroSessionTileFromHash key={key} filmId={key} showDate showVenue />
                ))}
                {removedItems.length > 0 && <div className={styles.heading()}>Removed from your watchlist</div>}
                {removedItems.map(([key]) => (
                  <MicroSessionTileFromHash key={key} filmId={key} showDate showVenue />
                ))}
                <T.Arrow className={styles.arrow()} />
              </motion.div>
            )}
          </AnimatePresence>
        </T.Content>
      </T.Portal>
    </T.Root>
  )
}

export const TooltipProvider = T.Provider

const tooltipStyles = tv({
  slots: {
    content: "theme-dark !z-[9999] radix-popover-animation",
    inner:
      "bg-bg text-fg py-[10px] px-[10px] flex flex-col gap-[8px] rounded-md radix-tooltip-shadow origin-top cursor-pointer",
    arrow: "fill-black",
    heading: "type-label-caps",
  },
  variants: {
    padded: {},
  },
})
